import { Link } from 'react-router-dom'
import PageFrame from '@/components/common/PageFrame'
import { Button, buttonVariants } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useDeskSummary } from '@/hooks/useDeskSummary'
import { accessOf, canFinance, canOpening, canSettlement } from '@/lib/admin-access'
import type { DeskSummary } from '@/lib/api-client'
import { homePath, PENDING_AGREEMENTS, PENDING_EKYC, PENDING_FUNDS } from '@/lib/nav'
import { useAuthStore } from '@/store/auth'

type Queue = { to: string; label: string; hint: string; count: (n: DeskSummary) => number }

// Same statuses the counts use (client-service PENDING_OPENING), so each list matches its number.
const AGREEMENTS: Queue = { to: PENDING_AGREEMENTS, label: 'Submitted agreements', hint: 'Applications waiting for review', count: (n) => n.pending_agreements }
const FUNDS: Queue[] = [
  { to: `${PENDING_FUNDS}&kind=DEPOSIT`, label: 'Pending deposits', hint: 'No decision yet', count: (n) => n.pending_deposits },
  { to: `${PENDING_FUNDS}&kind=WITHDRAW`, label: 'Pending withdrawals', hint: 'Client asked to withdraw', count: (n) => n.pending_withdrawals },
  { to: '/settlement/funds?status=PROCESSING', label: 'Processing funds', hint: 'Approved, waiting for the MT5 server', count: (n) => n.processing_funds },
]
const EKYC: Queue = { to: PENDING_EKYC, label: 'eKYC pending', hint: 'Identity checks still running', count: (n) => n.ekyc_pending }

function QueueList({ opening, settlement }: { opening?: boolean; settlement?: boolean }) {
  const role = accessOf(useAuthStore((s) => s.user))
  const q = useDeskSummary(settlement ? 'settlement' : 'opening')
  const queues = [
    ...(opening ? [AGREEMENTS] : []),
    ...(settlement ? FUNDS : []),
    ...(canOpening(role) || canSettlement(role) ? [EKYC] : []),
  ]

  return (
    <section aria-label="Queues" className="max-w-4xl overflow-hidden rounded-lg border border-border bg-card">
      <div className="grid grid-cols-[minmax(0,1fr)_6rem_6.5rem] gap-4 border-b border-border-strong bg-muted px-5 py-2.5 text-[13px] font-semibold text-muted-foreground max-sm:grid-cols-[minmax(0,1fr)_auto]">
        <span>Queue</span>
        <span className="text-right">Open</span>
        <span className="max-sm:hidden" />
      </div>
      {q.isLoading ? (
        <div aria-busy="true" className="space-y-4 p-5">
          <p className="text-sm text-muted-foreground">Loading queue counts…</p>
          <Skeleton className="h-3.5 w-3/5" />
          <Skeleton className="h-3.5 w-1/2" />
          <Skeleton className="h-3.5 w-3/5" />
        </div>
      ) : q.isError ? (
        <div role="alert" className="flex flex-col items-start gap-2 p-5">
          <p className="font-semibold">Could not load queue counts</p>
          <p className="text-sm text-muted-foreground">This does not mean the queues are empty. Open a list to check it directly, or try again.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void q.refetch()}>
              Try again
            </Button>
            {queues.slice(0, 2).map((queue) => (
              <Link key={queue.to} to={queue.to} className={buttonVariants({ variant: 'outline' })}>
                Open {queue.label.toLowerCase()}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <>
          {q.data && queues.every((queue) => queue.count(q.data) === 0) ? (
            <p role="status" className="border-b border-border px-5 py-3 text-sm text-muted-foreground">
              Nothing is waiting. Every queue is empty right now.
            </p>
          ) : null}
        <ul>
          {queues.map((queue) => (
            <li key={queue.to} className="border-b border-border last:border-b-0">
              <Link
                to={queue.to}
                className="grid grid-cols-[minmax(0,1fr)_6rem_6.5rem] items-center gap-4 px-5 py-3.5 hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring max-sm:grid-cols-[minmax(0,1fr)_auto]"
              >
                <span className="min-w-0">
                  <span className="block text-[15px] font-semibold">{queue.label}</span>
                  <span className="block text-[13px] text-muted-foreground">{queue.hint}</span>
                </span>
                <span className="text-right font-mono text-[26px] leading-[30px] font-semibold tabular-nums">{q.data ? queue.count(q.data) : '—'}</span>
                <span className="justify-self-end text-sm font-semibold text-link max-sm:hidden">Open list</span>
              </Link>
            </li>
          ))}
        </ul>
        </>
      )}
    </section>
  )
}

export function DashboardPage() {
  const role = accessOf(useAuthStore((s) => s.user))
  return (
    <PageFrame title="Dashboard" description="Open items across the desk. Select a queue to see the matching list.">
      <QueueList opening={canOpening(role)} settlement={canFinance(role)} />
    </PageFrame>
  )
}

export function OpeningHomePage() {
  return (
    <PageFrame title="Opening Home" description="Applications waiting for review. Approving is final.">
      <QueueList opening />
    </PageFrame>
  )
}

export function SettlementHomePage() {
  return (
    <PageFrame title="Settlement Home" description="Client deposit and withdrawal requests waiting for a decision. Open a queue to see the matching list.">
      <QueueList settlement />
    </PageFrame>
  )
}

export function useHomePath() {
  return homePath(accessOf(useAuthStore((s) => s.user)))
}
